import { Fragment } from 'react'
import { useRouter } from 'next/router'

import SettingsFormProvider, { UserSettingsFields } from '~/contexts/SettingsFormContext'
import StudentSettingsForm from '~/panels/student/components/StudentSettingsForm'
import { BookingLanguage, City } from '~/types/booking'
import { constructPhoneNumberWithCallingCode, isValidPhoneNumber } from '~/utils/helpers'
import useCurrentUser from '~/hooks/useCurrentUser'
import { useGetStudentQuery, useEditStudentMutation } from '~/redux/apis/student-api'

export default function StudentSettings(): React.ReactElement | null {
  const router = useRouter()
  const user = useCurrentUser()
  const { data: student } = useGetStudentQuery({ studentId: user?.id as number }, { skip: !user })
  const [editStudent] = useEditStudentMutation()

  const onSubmit = async (fields: UserSettingsFields): Promise<void> => {
    const phoneWithCallerCode = constructPhoneNumberWithCallingCode(fields.countryCallingCode, fields.phone)
    const { city, fullName, languagePrimary, languageSecondary, timeslots, subjects, timezone, traits, schoolCountry } =
      fields

    if (!isValidPhoneNumber(phoneWithCallerCode) || !schoolCountry) {
      return
    }

    await editStudent({
      studentId: user?.id as number,
      preference: {
        city: city as City,
        languagePrimary: languagePrimary as BookingLanguage,
        languageSecondary: languageSecondary as BookingLanguage | undefined,
        timeslots,
        subjects,
        schoolCountry,
        traits,
      },
      user: { fullName, phone: phoneWithCallerCode, timezone },
    })

    router.reload()
  }

  if (!student) {
    return <Fragment />
  }

  return (
    <SettingsFormProvider formType="student-settings" onSubmit={onSubmit} user={student}>
      <StudentSettingsForm />
    </SettingsFormProvider>
  )
}
